import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate, useParams, useOutletContext } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { usePrintStore } from '@renderer/stores/printStore'
import cogoToast from '@successtar/cogo-toast'
import PrintPreview, { PrintPreviewRef } from '@renderer/components/PrintPreview'
import StickerEditor from '@renderer/components/StickerEditor'
import { FrameOption, FrameConfig } from '@renderer/types/configState'
import { COUNTDOWN_PLACEHOLDER, useCountdown } from '@renderer/utils/countdownUtils'
import { useTranslation } from 'react-i18next'
import { FaUndo, FaRedo, FaTrash } from 'react-icons/fa'
import { Button, ProcessingModal } from '@renderer/components'
import { backgroundVideoProcessor } from '@renderer/utils/backgroundVideoProcessor'
import type { PhotoState } from '@renderer/utils/generateFramedVideo'
import { initiatePrint } from '@renderer/utils/pipeline/printService'
import {
  processAndUploadStrip,
  processAndUploadPhotos,
  stateUploader
} from '@renderer/utils/pipeline/outputOrchestrator'
import type { ProcessingContext } from '@renderer/types/outputPipeline.types'
import { useSharedCountdown } from './hooks/useSharedCountdown'
import { useDesignLayout, useSessionLayout } from '@renderer/hooks'
import { BackgroundContainer, GlassPanel } from '@renderer/components/BackgroundContainer'
import { useBackgroundTextColor } from '@renderer/hooks/useBackgroundTextColor'
import { getColorBrightness } from '@renderer/utils/colorUtils'
import { waitPrintResultMinDisplay } from '@renderer/utils/printResultMinDisplay'
import { resolveFramedVideoPlaybackAndRemote } from '@renderer/utils/galleryUrlUtils'
import { navigateAfterOutputsReady } from '@renderer/utils/navigateAfterOutputs'

// Brush type preview component
const BrushTypePreview: React.FC<{
  brushType: BrushType
  brushColor: string
  brushSize: number
  isSelected: boolean
  onSelect: () => void
  accentColor?: string
}> = ({ brushType, brushColor, brushSize, isSelected, onSelect, accentColor = '#3B82F6' }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (canvas) {
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.clearRect(0, 0, 80, 40)
        ctx.strokeStyle = brushColor
        ctx.lineWidth = brushSize / 3
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'

        switch (brushType) {
          case 'dashed':
            ctx.setLineDash([4, 4])
            break
          case 'dotted':
            ctx.setLineDash([2, 3])
            break
          case 'marker':
            ctx.globalAlpha = 0.6
            ctx.lineWidth = (brushSize / 3) * 1.5
            break
          case 'pencil':
            ctx.globalAlpha = 0.8
            ctx.lineWidth = (brushSize / 3) * 0.8
            break
          case 'airbrush':
            ctx.globalAlpha = 0.5
            ctx.lineWidth = (brushSize / 3) * 1.2
            break
          default:
            ctx.setLineDash([])
        }

        ctx.beginPath()
        ctx.moveTo(10, 20)
        ctx.quadraticCurveTo(40, 10, 70, 20)
        ctx.stroke()
        ctx.globalAlpha = 1
        ctx.setLineDash([])
      }
    }
  }, [brushColor, brushSize, brushType])

  return (
    <button
      onClick={onSelect}
      type="button"
      className={`rounded-lg border-2 p-4 transition-all ${
        isSelected ? '' : 'border-gray-200 hover:border-gray-300'
      }`}
      style={
        isSelected
          ? {
              borderColor: accentColor,
              backgroundColor:
                accentColor.length === 7 && accentColor.startsWith('#')
                  ? `${accentColor}18`
                  : 'rgba(59, 130, 246, 0.08)'
            }
          : undefined
      }
    >
      <div className="flex flex-col items-center gap-2">
        <canvas
          ref={canvasRef}
          width={80}
          height={40}
          className="border border-gray-200 rounded bg-white"
        />
        <span className="text-xs font-medium text-carbon capitalize">{brushType}</span>
      </div>
    </button>
  )
}

// Sticker thumbnail component to handle loading state
const StickerThumbnail: React.FC<{
  sticker: { _id: string; name: string; thumbnail?: string; url?: string }
  thumbnail: string
  isSelected: boolean
  onSelect: () => void
  t: ReturnType<typeof useTranslation>['t']
  accentColor?: string
}> = ({ sticker, thumbnail, isSelected, onSelect, t, accentColor = '#3B82F6' }) => {
  const [imageError, setImageError] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)

  // Reset state when thumbnail changes
  useEffect(() => {
    setImageError(false)
    setImageLoaded(false)
  }, [thumbnail])

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`relative rounded-lg border-2 p-2 transition-all ${
        isSelected ? '' : 'border-gray-200 hover:border-gray-300'
      }`}
      style={
        isSelected
          ? {
              borderColor: accentColor,
              backgroundColor:
                accentColor.length === 7 && accentColor.startsWith('#')
                  ? `${accentColor}18`
                  : 'rgba(59, 130, 246, 0.08)'
            }
          : undefined
      }
    >
      {thumbnail && !imageError ? (
        <>
          {!imageLoaded && (
            <div className="w-full aspect-square bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs absolute inset-0">
              {t('edit.loading', 'Loading...')}
            </div>
          )}
          <img
            key={`${sticker._id}-${thumbnail}`}
            src={thumbnail}
            alt={sticker.name}
            className={`w-full h-auto rounded transition-opacity ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            crossOrigin={thumbnail.startsWith('http') ? 'anonymous' : undefined}
            onError={() => {
              console.error(`Failed to load sticker thumbnail: ${thumbnail}`, {
                stickerId: sticker._id,
                thumbnail: sticker.thumbnail,
                url: sticker.url
              })
              setImageError(true)
            }}
            onLoad={() => {
              setImageLoaded(true)
              console.log(`Sticker thumbnail loaded: ${sticker._id}`, thumbnail)
            }}
          />
        </>
      ) : (
        <div className="w-full aspect-square bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">
          {t('edit.loading', 'Loading...')}
        </div>
      )}
    </button>
  )
}

interface UserContext {
  user?: {
    email: string
  }
}

interface StickerPlacement {
  id: string
  stickerId: string
  x: number
  y: number
  size: number
  timestamp: number
}

type BrushType = 'solid' | 'dashed' | 'dotted' | 'marker' | 'pencil' | 'airbrush'

interface BrushStroke {
  id: string
  points: Array<{ x: number; y: number }>
  color: string
  size: number
  brushType: BrushType
  timestamp: number
}

const EditPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const context = useOutletContext<UserContext | null>()
  const {
    gallery,
    galleryKey,
    setSessionState,
    userPreferences,
    id,
    logActivity
  } = useSessionStateStore()
  const {
    getFrame,
    frames,
    config: globalConfig,
    getSticker,
    stickers,
    stickersLoaded,
    loadingProgress
  } = useConfigStore()
  const { downloadLocal } = useSettingsStore()
  const { isPrintingEnabled, getPrinterForFrame } = usePrintStore()

  const config = globalConfig?.pages.edit
  const isPageEnabled = config?.settings?.enabled === true
  const editTimeout = config?.settings.editTimeout || 0
  const captureMode = globalConfig?.pages.capture?.settings?.mode || 'standard'
  const captureTimeout = globalConfig?.pages.capture?.settings?.captureTimeout || 0
  const hasGlobalTimeout = captureMode === 'frame-based' && captureTimeout > 0
  
  // Use shared countdown for frame-based mode
  const { remainingTime: sharedRemainingTime, isExpired: isSharedCountdownExpired } = useSharedCountdown()
  
  const { primaryColor } = globalConfig?.theme || {
    primaryColor: '#3B82F6',
    secondaryColor: '#FFFFFF'
  }
  const DEFAULT_PAGE_BG = '#F8FAFC'
  const themeBgRaw = globalConfig?.theme?.backgroundColor?.trim()
  const hasBackgroundImage = Boolean(globalConfig?.theme?.backgroundImageUrl)
  const solidBgDiffersFromDefault =
    themeBgRaw !== undefined &&
    themeBgRaw.length > 0 &&
    Math.abs(getColorBrightness(themeBgRaw) - getColorBrightness(DEFAULT_PAGE_BG)) > 0.5
  /** Frosted shell on image or non-default solid color (same idea as dynamic background) */
  const editGlass = hasBackgroundImage || solidBgDiffersFromDefault
  const textColor = useBackgroundTextColor()
  const editTimerUsesDarkChrome =
    textColor.replace(/\s/g, '').toLowerCase() === '#ffffff' ||
    textColor.replace(/\s/g, '').toLowerCase() === '#fff'
  const editMetaRowClass = editGlass
    ? 'flex justify-between items-center text-sm font-medium text-carbon/90'
    : 'flex justify-between items-center text-sm text-carbon/60'
  const editToolbarIconEnabled = editGlass
    ? 'text-carbon transition-colors hover:opacity-90'
    : 'text-carbon/75 transition-colors hover:text-carbon'
  const editTabRowBorder = editGlass ? 'border-carbon/30' : 'border-gray-200'
  const editTabInactiveClass = editGlass
    ? 'border-transparent text-carbon/85 hover:border-carbon/40 hover:text-carbon'
    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
  const editSliderTrackClass = editGlass ? 'bg-carbon/25' : 'bg-gray-200'
  const editInsetPanelClass = editGlass ? 'border-carbon/35 bg-white/96' : 'border-gray-300 bg-white'

  const {
    isPortrait,
    isCompact,
    isTiny,
    designW: screenWidth,
    designH: screenHeight
  } = useDesignLayout()
  const { isCompact: viewportCompact, isLandscapeViewport } = useSessionLayout()
  /** Physical phone/tablet in landscape — right pane must scroll (short viewport height). */
  const editViewportLandscape = viewportCompact && isLandscapeViewport
  const stackEdit = isCompact && isPortrait
  /** Shared session timer — match Select.tsx frame-based / global countdown */
  const frameSessionTimerCompact = isCompact || isTiny
  const sessionCountdownTop = Math.max(screenHeight * 0.02, 32)
  const sessionCountdownRight = Math.max(screenWidth * 0.02, 32)
  const sessionCountdownFontSize = Math.min(Math.max(screenHeight * 0.022, 22), 34)

  const renderEditSharedSessionTimer = (): React.ReactElement => (
    <div
      className={`absolute z-50 ${frameSessionTimerCompact ? 'right-3 top-3 sm:right-4 sm:top-4' : ''}`}
      style={
        frameSessionTimerCompact
          ? undefined
          : { top: `${sessionCountdownTop}px`, right: `${sessionCountdownRight}px` }
      }
    >
      {editTimerUsesDarkChrome ? (
        frameSessionTimerCompact ? (
          <div className="rounded-lg border border-white/35 bg-black/60 px-2.5 py-1.5 text-center shadow-lg backdrop-blur-sm">
            <div className="text-[10px] font-medium uppercase tracking-wide text-white/75">
              {t('capture.timeRemaining', 'Time remaining')}
            </div>
            <div className="font-mono text-base font-semibold tabular-nums leading-none text-white drop-shadow-md">
              {sharedRemainingTime}
            </div>
          </div>
        ) : (
          <div className="rounded-lg border-2 border-white/90 bg-black/40 p-3 text-center shadow-lg backdrop-blur-sm sm:p-4">
            <div className="mb-1 text-sm text-white/90">
              {t('capture.timeRemaining', 'Time remaining')}
            </div>
            <div
              className="font-bold font-mono tabular-nums text-white"
              style={{ fontSize: `${sessionCountdownFontSize}px` }}
            >
              {sharedRemainingTime}
            </div>
          </div>
        )
      ) : frameSessionTimerCompact ? (
        <div
          className="rounded-lg border bg-white/85 px-2.5 py-1.5 text-center shadow-lg backdrop-blur-sm"
          style={{ borderColor: textColor }}
        >
          <div
            className="text-[10px] font-medium uppercase tracking-wide opacity-75"
            style={{ color: textColor }}
          >
            {t('capture.timeRemaining', 'Time remaining')}
          </div>
          <div
            className="font-mono text-base font-semibold tabular-nums leading-none"
            style={{ color: textColor }}
          >
            {sharedRemainingTime}
          </div>
        </div>
      ) : (
        <div
          className="rounded-lg border-2 bg-white/80 p-3 text-center shadow-lg backdrop-blur-sm sm:p-4"
          style={{ borderColor: textColor }}
        >
          <div className="mb-1 text-sm opacity-90" style={{ color: textColor }}>
            {t('capture.timeRemaining', 'Time remaining')}
          </div>
          <div
            className="font-mono font-bold tabular-nums"
            style={{ fontSize: `${sessionCountdownFontSize}px`, color: textColor }}
          >
            {sharedRemainingTime}
          </div>
        </div>
      )}
    </div>
  )
  const editSubtitleClass = editGlass
    ? stackEdit
      ? 'text-xs font-medium leading-snug text-carbon/95'
      : 'text-base font-medium text-carbon/95'
    : stackEdit
      ? 'text-xs text-carbon/60'
      : 'text-carbon/60'
  /** Short physical viewport — minimize header chrome */
  const editHeaderPadClass = editViewportLandscape
    ? 'px-3 py-1.5'
    : stackEdit
      ? 'px-4 py-3'
      : 'px-8 py-6'
  const editTitleClass =
    `font-bold text-carbon ${editGlass ? 'drop-shadow-sm ' : ''}` +
    (editViewportLandscape
      ? 'text-sm leading-tight tracking-tight'
      : stackEdit
        ? 'mb-1 text-xl'
        : 'mb-2 text-3xl')
  const editSubtitleVisibleClass = editViewportLandscape ? 'sr-only' : editSubtitleClass
  const editHeaderTimerClass =
    `font-bold font-mono tabular-nums text-carbon ` +
    (editViewportLandscape ? 'text-sm' : stackEdit ? 'text-lg' : 'text-2xl')
  const editMainContentClass = editViewportLandscape
    ? stackEdit
      ? 'flex min-h-0 flex-1 flex-col gap-2 p-2'
      : 'flex min-h-0 flex-1 flex-row gap-3 p-3'
    : stackEdit
      ? 'flex min-h-0 flex-1 flex-col gap-3 p-3'
      : 'flex min-h-0 flex-1 flex-row gap-6 p-6'
  /** Match Result.tsx primary sizing; compact + mobile landscape use dense control */
  const editContinueBtnClass =
    isCompact || editViewportLandscape
      ? 'w-full !px-4 !py-2 !text-sm'
      : isPortrait
        ? 'w-full !px-6 !py-3 !text-lg'
        : 'w-full !px-8 !py-4 !text-xl'
  const stickerGridClass = stackEdit
    ? 'grid grid-cols-4 gap-2'
    : isCompact
      ? 'grid grid-cols-2 gap-2'
      : 'grid grid-cols-3 gap-3'
  const brushTypeGridClass = stackEdit ? 'grid grid-cols-3 gap-2' : 'grid grid-cols-2 gap-3'
  const sidebarWidthClass = stackEdit
    ? 'w-full'
    : isCompact
      ? editViewportLandscape
        ? 'w-52 min-w-[13rem]'
        : 'w-48'
      : 'w-80'
  /** Single scroll body between tabs and Continue; outer pane stays flex-contained */
  const editSidebarShellClass = stackEdit
    ? `flex min-h-0 shrink-0 flex-col gap-2 overflow-hidden ${
        editViewportLandscape ? 'max-h-[55%]' : 'max-h-[45%]'
      }`
    : `flex min-h-0 max-h-full shrink-0 flex-col overflow-hidden ${isCompact ? 'gap-4' : 'gap-6'}`
  const sectionTitleClass = stackEdit ? 'mb-1 text-base' : 'mb-3 text-lg'
  /** Phone / compact canvas / narrow landscape — stack toolbar vertically + smaller glyphs */
  const editFloatingToolbarMobile = isCompact || editViewportLandscape
  const iconSize = editFloatingToolbarMobile ? 'text-xs' : 'text-[2em]'
  const editToolbarFloatClass = editViewportLandscape
    ? stackEdit
      ? 'left-1.5 top-1.5 gap-0.5'
      : 'right-2 top-2 gap-0.5'
    : stackEdit
      ? 'left-2 top-2 gap-0.5'
      : 'right-4 top-4 gap-3'
  const editToolbarBtnClass = editFloatingToolbarMobile
    ? 'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md'
    : ''

  // States
  const [selectedStickerId, setSelectedStickerId] = useState<string>('')
  const [stickerPlacements, setStickerPlacements] = useState<StickerPlacement[]>([])
  const [brushStrokes, setBrushStrokes] = useState<BrushStroke[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [isGeneratingStrip, setIsGeneratingStrip] = useState(true)
  const [stripUrl, setStripUrl] = useState<string>('')
  const [undoStack, setUndoStack] = useState<
    Array<{ stickers: StickerPlacement[]; brushes: BrushStroke[] }>
  >([])
  const [redoStack, setRedoStack] = useState<
    Array<{ stickers: StickerPlacement[]; brushes: BrushStroke[] }>
  >([])
  const [remainingTime, setRemainingTime] = useState<string>(COUNTDOWN_PLACEHOLDER)
  const [editMode, setEditMode] = useState<'sticker' | 'brush'>('sticker')
  const [brushColor, setBrushColor] = useState<string>('#000000')
  const [brushSize, setBrushSize] = useState<number>(10)
  const [selectedBrushType, setSelectedBrushType] = useState<BrushType>('solid')
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
  const [selectedItemType, setSelectedItemType] = useState<'sticker' | 'brush' | null>(null)

  const previewRef = useRef<PrintPreviewRef>(null)
  const isMountedRef = useRef(true)
  const isProcessingRef = useRef(false)
  const navigationLockRef = useRef(false)
  const videoProcessingIdRef = useRef<string | null>(null)
  const colorPickerRef = useRef<HTMLInputElement>(null)

  // Sticker size (using slider instead of fixed sizes)
  const [stickerSize, setStickerSize] = useState<number>(150) // Default size in pixels
  const minSize = 50
  const maxSize = 300

  // Get available stickers filtered by stickerIds from config
  const availableStickers = React.useMemo(() => {
    if (!config?.settings.stickerIds || config.settings.stickerIds.length === 0) {
      return []
    }
    return stickers.filter((sticker) => config.settings.stickerIds.includes(sticker._id))
  }, [stickers, config?.settings.stickerIds])

  // Force re-render when stickers are updated in store
  // This ensures the UI updates when stickers finish loading
  // Only log, don't cause state updates that would trigger re-renders
  useEffect(() => {
    // This effect will trigger a re-render whenever stickers or loadingProgress changes
    // The component will re-evaluate availableStickers and their loading state
    console.log('[Edit] Stickers updated:', {
      totalStickers: stickers.length,
      availableStickers: availableStickers.length,
      stickerIds: config?.settings.stickerIds,
      loadingProgress: { loaded: loadingProgress.loaded, total: loadingProgress.total }
    })
    // No state updates here - just logging
  }, [
    stickers.length, // Use length instead of array reference
    loadingProgress.loaded,
    loadingProgress.total,
    availableStickers.length,
    config?.settings.stickerIds?.join(',') // Use string instead of array reference
  ])

  // Initialize countdown hook for timeout
  const editCountdown = useCountdown(editTimeout)

  // Get frame configuration
  const getFrameConfig = (frame: FrameOption | null): FrameConfig => {
    if (!frame)
      return {
        id: '',
        baseImage: '',
        drawParams: {
          canvasWidth: 1200,
          canvasHeight: 1800,
          backgroundColor: '#FFFFFF',
          images: []
        }
      }
    return {
      id: frame._id,
      baseImage: frame.thumbnail,
      drawParams: frame.drawParams
    }
  }

  const selectedFrame = getFrame(userPreferences.frameId || frames[0]?._id) || null
  const frameConfig = getFrameConfig(selectedFrame)

  // Get captured photos for preview - memoize to prevent infinite loops
  const capturedPhotos = React.useMemo(
    () => gallery.capturedItems.filter((item) => item.photoUrl).map((item) => item.photoUrl),
    [gallery.capturedItems]
  )

  // Use the existing filtered strip from gallery or generate it
  useEffect(() => {
    if (gallery.stripUrl) {
      setStripUrl((prev) => {
        // Only update if URL actually changed
        if (prev === gallery.stripUrl) return prev
        return gallery.stripUrl || ''
      })
      setIsGeneratingStrip(false)
      return
    }

    // Only generate if we don't already have a stripUrl set
    if (stripUrl) return

    // Fallback: generate strip if none exists
    let isMounted = true
    const generateStrip = async (): Promise<void> => {
      setIsGeneratingStrip(true)
      try {
        // Wait for PrintPreview to be ready
        if (previewRef.current) {
          try {
            await Promise.race([
              previewRef.current.waitForReady(),
              new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('PrintPreview timeout')), 5000)
              )
            ])
          } catch (error) {
            console.warn('PrintPreview waitForReady timeout or error:', error)
          }
        } else {
          // Wait a bit if PrintPreview ref is not ready
          await new Promise((resolve): void => {
            setTimeout(resolve, 1000)
          })
        }

        if (!isMounted) return

        // Generate the strip
        const stripBlob = await previewRef.current?.exportImage()
        if (stripBlob && isMounted) {
          const newStripUrl = URL.createObjectURL(stripBlob)
          setStripUrl((prev) => {
            // Only update if URL actually changed
            if (prev === newStripUrl) return prev
            return newStripUrl
          })
        } else if (!stripBlob) {
          throw new Error('Failed to generate strip')
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error generating strip:', error)
          cogoToast.error('Failed to generate photo strip')
        }
      } finally {
        if (isMounted) {
          setIsGeneratingStrip(false)
        }
      }
    }

    generateStrip()

    return (): void => {
      isMounted = false
    }
  }, [gallery.stripUrl]) // Removed capturedPhotos from dependencies

  // Set selected sticker to first available if none selected
  useEffect(() => {
    if (availableStickers.length > 0 && !selectedStickerId) {
      setSelectedStickerId(availableStickers[0]._id)
    }
  }, [availableStickers, selectedStickerId])

  // Handle global countdown expiration - auto-continue
  useEffect(() => {
    if (
      hasGlobalTimeout &&
      isSharedCountdownExpired &&
      !isProcessing &&
      !isGeneratingStrip &&
      !navigationLockRef.current &&
      isMountedRef.current
    ) {
      navigationLockRef.current = true
      logActivity('Global countdown expired on Edit page, auto-continuing')
      handleContinue().finally(() => {
        // Reset lock after navigation completes
        setTimeout(() => {
          navigationLockRef.current = false
        }, 1000)
      })
    }
  }, [hasGlobalTimeout, isSharedCountdownExpired, isProcessing, isGeneratingStrip])

  // Timeout handling - only if no global timeout
  useEffect(() => {
    if (!isPageEnabled || hasGlobalTimeout || editTimeout <= 0) return

    editCountdown.setOnTick(() => {
      setRemainingTime(editCountdown.getFormattedRemainingSeconds())
    })

    editCountdown.setOnComplete(() => {
      if (!isMountedRef.current) return
      logActivity('Edit timeout expired, navigating to result')
      handleContinue()
    })

    editCountdown.start()

    return (): void => {
      editCountdown.stop()
      editCountdown.setOnComplete(null)
      editCountdown.setOnTick(null)
    }
  }, [isPageEnabled, hasGlobalTimeout, editTimeout])

  // Handle sticker placement with useCallback to prevent unnecessary re-renders
  const handleStickerPlacement = useCallback(
    (x: number, y: number): void => {
      if (!selectedStickerId) return

      const newPlacement: StickerPlacement = {
        id: `sticker-${Date.now()}-${Math.random()}`,
        stickerId: selectedStickerId,
        x,
        y,
        size: stickerSize,
        timestamp: Date.now()
      }

      // Add to undo stack
      setUndoStack((prev) => [
        ...prev,
        { stickers: [...stickerPlacements], brushes: [...brushStrokes] }
      ])
      setRedoStack([]) // Clear redo stack when new action is performed

      setStickerPlacements((prev) => [...prev, newPlacement])
      // Auto-select the newly placed sticker
      setSelectedItemId(newPlacement.id)
      setSelectedItemType('sticker')
    },
    [stickerSize, selectedStickerId, stickerPlacements, brushStrokes]
  )

  // Handle item selection
  const handleItemSelect = useCallback((itemId: string | null, itemType: 'sticker' | 'brush' | null): void => {
    setSelectedItemId(itemId)
    setSelectedItemType(itemType)
  }, [])

  // Handle sticker position update during drag
  const handleStickerUpdate = useCallback(
    (stickerId: string, x: number, y: number): void => {
      setStickerPlacements((prev) =>
        prev.map((p) => (p.id === stickerId ? { ...p, x, y } : p))
      )
    },
    []
  )

  // Handle brush stroke update during drag
  const handleBrushStrokeUpdate = useCallback(
    (strokeId: string, points: Array<{ x: number; y: number }>): void => {
      setBrushStrokes((prev) =>
        prev.map((s) => (s.id === strokeId ? { ...s, points } : s))
      )
    },
    []
  )

  // Handle delete selected item
  const handleDelete = useCallback((): void => {
    if (!selectedItemId || !selectedItemType) return

    // Add to undo stack
    setUndoStack((prev) => [
      ...prev,
      { stickers: [...stickerPlacements], brushes: [...brushStrokes] }
    ])
    setRedoStack([]) // Clear redo stack when new action is performed

    if (selectedItemType === 'sticker') {
      setStickerPlacements((prev) => prev.filter((p) => p.id !== selectedItemId))
    } else if (selectedItemType === 'brush') {
      setBrushStrokes((prev) => prev.filter((s) => s.id !== selectedItemId))
    }

    // Clear selection
    setSelectedItemId(null)
    setSelectedItemType(null)
  }, [selectedItemId, selectedItemType, stickerPlacements, brushStrokes])

  // Handle brush stroke
  const handleBrushStroke = useCallback(
    (stroke: BrushStroke): void => {
      // Add to undo stack
      setUndoStack((prev) => [
        ...prev,
        { stickers: [...stickerPlacements], brushes: [...brushStrokes] }
      ])
      setRedoStack([]) // Clear redo stack when new action is performed

      setBrushStrokes((prev) => [...prev, stroke])
    },
    [stickerPlacements, brushStrokes]
  )

  // Undo functionality
  const handleUndo = (): void => {
    if (undoStack.length === 0) return

    const previousState = undoStack[undoStack.length - 1]
    const newUndoStack = undoStack.slice(0, -1)

    setRedoStack((prev) => [
      ...prev,
      { stickers: [...stickerPlacements], brushes: [...brushStrokes] }
    ])
    setUndoStack(newUndoStack)
    setStickerPlacements(previousState.stickers)
    setBrushStrokes(previousState.brushes)
  }

  // Redo functionality
  const handleRedo = (): void => {
    if (redoStack.length === 0) return

    const nextState = redoStack[redoStack.length - 1]
    const newRedoStack = redoStack.slice(0, -1)

    setUndoStack((prev) => [
      ...prev,
      { stickers: [...stickerPlacements], brushes: [...brushStrokes] }
    ])
    setRedoStack(newRedoStack)
    setStickerPlacements(nextState.stickers)
    setBrushStrokes(nextState.brushes)
  }

  // Handle continue to result
  const handleContinue = async (): Promise<void> => {
    // Check processing lock
    if (isProcessingRef.current) {
      console.warn('[Edit] Already processing, ignoring duplicate request')
      return
    }
    
    if (!isMountedRef.current) return
    
    isProcessingRef.current = true
    setIsProcessing(true)
    editCountdown.stop()
    const processingStartedAt = Date.now()
    let didAttemptPrint = false

    // Set up timeout for processing (30 seconds max)
    let timeoutFired = false
    const processingTimeout = setTimeout(() => {
      timeoutFired = true
      console.warn('[Edit] Processing timeout exceeded, forcing navigation')
      if (isMountedRef.current) {
        setIsProcessing(false)
      }
      stateUploader.uploadSessionState().catch(() => {})
      if (isMountedRef.current) {
        navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
      }
    }, 30000)

    try {
      // Create a canvas to combine strip with stickers
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('Could not get canvas context')

      // Load the strip image - handle missing or invalid URLs
      let currentStripUrl = stripUrl

      if (!currentStripUrl || currentStripUrl.trim() === '') {
        // Try to regenerate strip if URL is missing
        console.warn('[Edit] No strip URL available, attempting to regenerate...')
        if (previewRef.current) {
          try {
            await Promise.race([
              previewRef.current.waitForReady(),
              new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('PrintPreview timeout')), 5000)
              )
            ])
            const stripBlob = await previewRef.current.exportImage()
            if (stripBlob) {
              const newStripUrl = URL.createObjectURL(stripBlob)
              setStripUrl(newStripUrl)
              currentStripUrl = newStripUrl
            } else {
              throw new Error('Failed to generate strip from PrintPreview')
            }
          } catch (error) {
            console.error('[Edit] Failed to regenerate strip:', error)
            throw new Error('No strip URL available and failed to regenerate')
          }
        } else {
          throw new Error('No strip URL available and PrintPreview not available')
        }
      }

      const stripImg = new Image()
      stripImg.crossOrigin = 'anonymous'

      await new Promise<void>((resolve, reject): void => {
        const timeout = setTimeout(() => {
          reject(new Error('Timeout loading strip image'))
        }, 10000) // 10 second timeout

        stripImg.onload = (): void => {
          clearTimeout(timeout)
          resolve()
        }
        stripImg.onerror = (): void => {
          clearTimeout(timeout)
          console.error('[Edit] Failed to load strip image:', {
            stripUrl: currentStripUrl,
            urlType: currentStripUrl.startsWith('blob:')
              ? 'blob'
              : currentStripUrl.startsWith('http')
                ? 'http'
                : 'other',
            urlLength: currentStripUrl.length
          })
          // If blob URL failed, try to regenerate
          if (currentStripUrl.startsWith('blob:') && previewRef.current) {
            console.log('[Edit] Blob URL failed, attempting to regenerate strip...')
            previewRef.current
              .exportImage()
              .then((blob) => {
                if (blob) {
                  const newUrl = URL.createObjectURL(blob)
                  setStripUrl(newUrl)
                  stripImg.src = newUrl
                } else {
                  reject(new Error('Failed to regenerate strip'))
                }
              })
              .catch((err) => {
                reject(new Error(`Failed to load strip image: ${err.message}`))
              })
          } else {
            reject(new Error(`Failed to load strip image: ${currentStripUrl.substring(0, 50)}...`))
          }
        }
        stripImg.src = currentStripUrl
      })

      // Set canvas size to match strip dimensions at full resolution
      // Use natural dimensions for highest quality
      canvas.width = stripImg.naturalWidth
      canvas.height = stripImg.naturalHeight

      // Enable high-quality image rendering
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'

      // Draw the strip at full resolution
      ctx.drawImage(stripImg, 0, 0, canvas.width, canvas.height)

      // Draw brush strokes first (behind stickers)
      for (const stroke of brushStrokes) {
        if (stroke.points.length === 0) continue

        ctx.save()
        ctx.strokeStyle = stroke.color
        let lineWidth = stroke.size
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'

        // Apply brush type styling with enhanced effects
        switch (stroke.brushType) {
          case 'dashed':
            ctx.setLineDash([Math.max(5, lineWidth * 0.3), Math.max(5, lineWidth * 0.3)])
            ctx.lineWidth = lineWidth
            break
          case 'dotted':
            ctx.setLineDash([Math.max(1, lineWidth * 0.1), Math.max(4, lineWidth * 0.2)])
            ctx.lineCap = 'round'
            ctx.lineWidth = lineWidth
            break
          case 'marker':
            ctx.globalAlpha = 0.6
            lineWidth = stroke.size * 1.8
            ctx.lineWidth = lineWidth
            // Add slight blur effect for marker
            ctx.shadowBlur = lineWidth * 0.3
            ctx.shadowColor = stroke.color
            break
          case 'pencil':
            ctx.globalAlpha = 0.85
            lineWidth = stroke.size * 0.7
            ctx.lineWidth = lineWidth
            // Add texture effect for pencil
            ctx.shadowBlur = lineWidth * 0.2
            ctx.shadowColor = stroke.color
            break
          case 'airbrush':
            ctx.globalAlpha = 0.4
            lineWidth = stroke.size * 1.5
            ctx.lineWidth = lineWidth
            // Add soft blur for airbrush
            ctx.shadowBlur = lineWidth * 0.5
            ctx.shadowColor = stroke.color
            break
          case 'solid':
          default:
            ctx.setLineDash([])
            ctx.lineWidth = lineWidth
            break
        }

        // Draw the stroke with smooth curves
        ctx.beginPath()
        const startPoint = stroke.points[0]
        ctx.moveTo(startPoint.x, startPoint.y)

        // For smoother curves, use quadratic curves between points
        if (stroke.points.length > 2) {
          for (let i = 1; i < stroke.points.length - 1; i++) {
            const currentPoint = stroke.points[i]
            const nextPoint = stroke.points[i + 1]
            const midX = (currentPoint.x + nextPoint.x) / 2
            const midY = (currentPoint.y + nextPoint.y) / 2
            ctx.quadraticCurveTo(currentPoint.x, currentPoint.y, midX, midY)
          }
          // Draw to last point
          const lastPoint = stroke.points[stroke.points.length - 1]
          ctx.lineTo(lastPoint.x, lastPoint.y)
        } else {
          // For short strokes, just draw a line
          for (let i = 1; i < stroke.points.length; i++) {
            const point = stroke.points[i]
            ctx.lineTo(point.x, point.y)
          }
        }

        ctx.stroke()

        // Reset shadow and alpha
        ctx.shadowBlur = 0
        ctx.globalAlpha = 1
        ctx.restore()
      }

      // Draw stickers
      for (const placement of stickerPlacements) {
        try {
          const sticker = getSticker(placement.stickerId)
          if (!sticker) {
            console.warn(`Sticker ${placement.stickerId} not found`)
            continue
          }

          // Use the cached URL from the store (which should be a blob URL if loaded)
          // Fallback to original URL if not cached yet
          const stickerUrl = sticker.url || ''
          if (!stickerUrl) {
            console.warn(`Sticker ${placement.stickerId} has no URL`)
            continue
          }

          const stickerImg = new Image()
          stickerImg.crossOrigin = 'anonymous'

          await new Promise<void>((resolve, reject): void => {
            stickerImg.onload = (): void => resolve()
            stickerImg.onerror = (): void => {
              console.error(`Failed to load sticker image: ${stickerUrl}`, {
                stickerId: placement.stickerId,
                url: stickerUrl
              })
              reject(new Error(`Failed to load sticker ${placement.stickerId}`))
            }
            stickerImg.src = stickerUrl
          })

          // Calculate sticker position (center the sticker at the placement point)
          // The placement coordinates from StickerEditor are in image pixel space
          // (StickerEditor's getClickPosition: scaleX = imageDimensions.width / canvas.width)
          //
          // The size is a display pixel value (80, 120, 180). In StickerEditor render:
          // - Image is displayed at drawWidth (scaled to fit canvas)
          // - scaleX = drawWidth / imageDimensions.width (image to display ratio, typically < 1)
          // - Sticker size in display: placement.size * Math.max(scaleX, scaleY)
          //
          // To convert size from display pixels to image pixels:
          // size_image = size_display / scaleX = size_display * (imageWidth / drawWidth)
          //
          // Since we don't know drawWidth, we'll estimate based on typical display
          // For HD displays, assume image is displayed at ~1000px width
          // This gives us a reasonable scale factor
          const estimatedDisplayWidth = Math.min(1000, stripImg.naturalWidth) // Cap at image width
          const sizeScale = stripImg.naturalWidth / estimatedDisplayWidth
          const baseScaledSize = placement.size * sizeScale

          // Maintain sticker aspect ratio
          const stickerAspect = stickerImg.naturalWidth / stickerImg.naturalHeight
          let stickerWidth: number
          let stickerHeight: number

          if (stickerAspect >= 1) {
            // Sticker is wider or square - use baseScaledSize as width
            stickerWidth = baseScaledSize
            stickerHeight = stickerWidth / stickerAspect
          } else {
            // Sticker is taller - use baseScaledSize as height
            stickerHeight = baseScaledSize
            stickerWidth = stickerHeight * stickerAspect
          }

          // Coordinates are already in image pixel space, so use directly
          const stickerX = placement.x - stickerWidth / 2
          const stickerY = placement.y - stickerHeight / 2

          // Draw sticker at full resolution with high quality, maintaining aspect ratio
          ctx.imageSmoothingEnabled = true
          ctx.imageSmoothingQuality = 'high'
          ctx.drawImage(stickerImg, stickerX, stickerY, stickerWidth, stickerHeight)
        } catch (error) {
          console.error('Failed to render sticker:', error)
        }
      }

      // Convert to blob with maximum quality
      const finalBlob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob)
            else reject(new Error('Failed to create final image blob'))
          },
          'image/jpeg',
          1.0 // Maximum quality (1.0 = 100%)
        )
      })

      // Note: Watermarks are only applied to individual photos, not strips
      // The final strip with stickers should remain clean

      // Check network status for print error suppression
      let isOnline = true
      if (typeof window !== 'undefined') {
        const bridge = window as typeof window & { api?: { getInternetStatus?: () => Promise<boolean> } }
        if (bridge.api?.getInternetStatus) {
          isOnline = await bridge.api.getInternetStatus()
        } else {
          isOnline = navigator.onLine
        }
      }

      // Print immediately after strip creation (blocking for initiation only, with feedback)
      if (isPrintingEnabled && userPreferences.usePrint && userPreferences.copies > 0) {
        didAttemptPrint = true
        const { printerName, settings } = getPrinterForFrame(userPreferences.frameId || '')
        logActivity('Sending print job for final strip with stickers', {
          copies: userPreferences.copies,
          printerName
        })

        // Initiate print and wait for job to be sent (blocking for initiation only)
        try {
          await initiatePrint(
            finalBlob,
            printerName,
            userPreferences.copies,
            settings,
            isOnline
          )
          cogoToast.success(t('edit.printInitiated', 'Printing Initialized'))
        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : String(error)
          cogoToast.error(`${t('edit.printFailed', 'Failed to print')}: ${errorMsg}`)
        }
      }

      // Process individual photos first (upload them) - fire-and-forget
      const currentGallery = useSessionStateStore.getState().gallery
      const watermarkUrl = globalConfig?.pages.capture?.settings?.watermarkUrl
      const editCtx: ProcessingContext = {
        context,
        projectId,
        sessionId: id || '',
        galleryId: galleryKey || gallery.id,
        downloadLocal,
        isOnline
      }
      const watermarkConfig =
        watermarkUrl?.trim() ?
          {
            watermarkUrl,
            watermarkHorizontalAnchor:
              globalConfig?.pages.capture?.settings?.watermarkHorizontalAnchor || 'left',
            watermarkVerticalAnchor:
              globalConfig?.pages.capture?.settings?.watermarkVerticalAnchor || 'top',
            watermarkHorizontalOffset:
              globalConfig?.pages.capture?.settings?.watermarkHorizontalOffset || 0,
            watermarkVerticalOffset:
              globalConfig?.pages.capture?.settings?.watermarkVerticalOffset || 0
          }
        : undefined
      if (!currentGallery.individualPhotosUploaded) {
        // Pre-assign captureIndex matching the upload filename (`photo-${index}.jpg`)
        // so PostSessionAI can map server AI outputs back to the right gallery item.
        // The filter mirrors processIndividualPhotos' internal "non-empty photoUrl" filter.
        const filteredForUpload = currentGallery.capturedItems.filter(
          (item) => item.photoUrl && item.photoUrl.trim() !== ''
        )
        const idToCaptureIndex = new Map<string, number>()
        filteredForUpload.forEach((item, idx) => {
          if (item.id) idToCaptureIndex.set(item.id, idx)
        })
        if (idToCaptureIndex.size > 0) {
          const g = useSessionStateStore.getState().gallery
          setSessionState({
            gallery: {
              ...g,
              capturedItems: g.capturedItems.map((it) => {
                const idx = idToCaptureIndex.get(it.id)
                return idx === undefined ? it : { ...it, captureIndex: idx }
              })
            }
          })
        }
        processAndUploadPhotos(
          currentGallery.capturedItems,
          editCtx,
          watermarkConfig,
          () => {
            if (isMountedRef.current) {
              cogoToast.error(t('edit.errorUploadingPhotos', 'Error uploading individual photos'))
            }
          }
        )
          .then(() => {
            if (!isMountedRef.current) return
            const g = useSessionStateStore.getState().gallery
            setSessionState({
              gallery: {
                ...g,
                individualPhotosUploaded: true,
                individualPhotosUploadedAt: Date.now()
              }
            })
          })
          .catch((err: unknown) =>
            console.warn('[Edit] processAndUploadPhotos (non-blocking):', err)
          )
      }

      // Upload the edited strip (non-blocking with timeout)
      const uploadPromise = processAndUploadStrip(finalBlob, editCtx)
      const timeoutPromise = new Promise<{ localUrl: string }>((resolve) => {
        setTimeout(() => resolve({ localUrl: URL.createObjectURL(finalBlob) }), 10000)
      })
      const stripResult = await Promise.race([
        uploadPromise.then((r) => (r ? { localUrl: r.localUrl } : { localUrl: URL.createObjectURL(finalBlob) })),
        timeoutPromise
      ])

      // Add edited strip to gallery (keep plain strip if it exists)
      const itemsWithoutEditedStrips = currentGallery.capturedItems.filter(
        (item) => !item.id?.includes('dreambooth-strip-with-stickers')
      )

      // Always add the edited strip to capturedItems so Result page can find it
      const editedStripItem = {
        id: `dreambooth-strip-with-stickers-${id}`,
        stripUrl: stripResult.localUrl || stripUrl,
        photoUrl: '',
        timestamp: new Date()
      }

      setSessionState({
        gallery: {
          capturedItems: [...itemsWithoutEditedStrips, editedStripItem],
          stripUrl: stripResult.localUrl || stripUrl // Use edited strip as the main strip URL
        }
      })

      // Trigger video processing with final strip (with stickers) if enabled
      if (
        userPreferences.useRecording &&
        ['strip_live', 'strip_story'].includes(userPreferences.recordingType || '')
      ) {
        console.log('[Edit] Triggering video processing with final strip (includes stickers)')

        // Get photos from gallery for video processing
        const photosForVideo = gallery.capturedItems
          .filter((item) => item.photoUrl && !item.id?.includes('dreambooth-strip'))
          .map((item) => item.photoUrl!)

        const resources = photosForVideo.map((photoUrl) => {
          const item = gallery.capturedItems.find((item) => item.photoUrl === photoUrl)
          return {
            photoUrl: photoUrl,
            videoUrl: item?.recordingUrl || ''
          }
        })

        const photoStates: Record<string, PhotoState> =
          useSessionStateStore.getState().gallery.photoStates ||
          previewRef.current?.getPhotoStates() ||
          {}

        const taskId = `video-${userPreferences.recordingType}-final-${id}-${Date.now()}`

        // Use unscaled frame config for video (same as Select page)
        const unscaledFrame = getFrame(userPreferences.frameId || frames[0]?._id, 1)
        const unscaledFrameConfig = unscaledFrame
          ? {
              id: unscaledFrame._id,
              baseImage: unscaledFrame.thumbnail,
              drawParams: unscaledFrame.drawParams
            }
          : frameConfig

        // Use backgroundVideoProcessor (imported at top)
        videoProcessingIdRef.current = taskId
        backgroundVideoProcessor.processVideo({
          id: taskId,
          type: userPreferences.recordingType === 'strip_story' ? 'story' : 'live',
          resources,
          frameConfig: unscaledFrameConfig,
          sessionId: id || '',
          userId: context?.user?.email || '',
          projectId: projectId || '',
          galleryId: galleryKey || gallery.id,
          speed: globalConfig?.pages.capture?.settings?.recordingSpeed || 1,
          fps: 30,
          photoStates: photoStates,
          countdown: globalConfig?.pages.capture?.settings?.captureCountdown || 1000,
          onComplete: (uploadedUrl, localUrl) => {
            const { playbackUrl, videoRemoteUrl } = resolveFramedVideoPlaybackAndRemote(
              uploadedUrl,
              localUrl
            )
            if (!playbackUrl) return
            const prevItems = useSessionStateStore.getState().gallery.capturedItems.filter(
              (i) => i.id !== taskId
            )
            setSessionState({
              gallery: {
                capturedItems: [
                  ...prevItems,
                  {
                    id: taskId,
                    videoUrl: playbackUrl,
                    videoRemoteUrl,
                    photoUrl: '',
                    timestamp: new Date()
                  }
                ]
              }
            })
            logActivity('Framed strip video ready (edited)', {
              taskId,
              hasRemoteUrl: Boolean(uploadedUrl && uploadedUrl.length > 0)
            })
          },
          onError: (error) => {
            if (!isMountedRef.current) return
            
            const errorMessage = error instanceof Error ? error.message : 'Unknown error'
            console.error('[Edit] Error processing video with final strip:', errorMessage, {
              taskId,
              recordingType: userPreferences.recordingType,
              photosCount: photosForVideo.length
            })
            logActivity(`Video processing failed: ${errorMessage}`, {
              taskId,
              recordingType: userPreferences.recordingType,
              photosCount: photosForVideo.length,
              error: errorMessage,
              level: 'error'
            })
            if (isMountedRef.current) {
              cogoToast.error('Error processing video with stickers')
            }
            videoProcessingIdRef.current = null
          }
        })
      }

      stateUploader.uploadSessionState().catch(() => {})

      // Clear timeout before optional minimum dwell so the 30s cap cannot fire mid-wait
      clearTimeout(processingTimeout)

      // Don't navigate if timeout already fired
      if (timeoutFired) {
        isProcessingRef.current = false
        return
      }

      const minDisplaySec = useSettingsStore.getState().printResultMinDisplaySeconds
      if (didAttemptPrint && minDisplaySec > 0) {
        await waitPrintResultMinDisplay(processingStartedAt, minDisplaySec, {
          isStillMounted: () => isMountedRef.current
        })
      }

      // Reset processing state before navigation to prevent stuck state
      if (isMountedRef.current) {
        setIsProcessing(false)
      }

      // Small delay to ensure state is updated before navigation
      await new Promise((resolve) => setTimeout(resolve, 100))

      if (isMountedRef.current) {
        navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
      }
      isProcessingRef.current = false
    } catch (error) {
      console.error('Error in handleContinue:', error)
      cogoToast.error('Failed to process strip with stickers')

      // Clear timeout on error
      clearTimeout(processingTimeout)

      // Don't navigate if timeout already fired
      if (timeoutFired) {
        return
      }

      // Reset processing state even on error
      if (isMountedRef.current) {
        setIsProcessing(false)
      }

      // Still navigate even if there's an error
      stateUploader.uploadSessionState().catch(() => {})

      // Small delay to ensure state is updated before navigation
      await new Promise((resolve) => setTimeout(resolve, 100))

      if (isMountedRef.current) {
        navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
      }
      isProcessingRef.current = false
    }
  }

  // Redirect if page is disabled
  if (!isPageEnabled) {
    console.log('[Edit] Page is disabled, redirecting to Result')
    navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
    return null
  }

  // Redirect if no stickers available
  // Check if stickers are still loading before redirecting
  const isStickersLoading = !stickersLoaded || loadingProgress.loaded < loadingProgress.total

  if (availableStickers.length === 0) {
    // Only redirect if stickers are fully loaded (or failed to load) and still empty
    if (stickersLoaded && !isStickersLoading) {
      console.warn('[Edit] No stickers available after loading, redirecting to Result', {
        totalStickers: stickers.length,
        stickerIds: config?.settings.stickerIds,
        stickersLoaded,
        loadingProgress: { loaded: loadingProgress.loaded, total: loadingProgress.total }
      })
      navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
      return null
    } else {
      console.log('[Edit] No stickers available yet, waiting for load to complete...', {
        stickersLoaded,
        loaded: loadingProgress.loaded,
        total: loadingProgress.total,
        totalStickers: stickers.length
      })
    }
  }

  function editShell(inner: React.ReactNode): React.ReactElement {
    if (editGlass) {
      return (
        <GlassPanel
          className={`flex min-h-0 flex-1 flex-col overflow-hidden ${
            isCompact ? 'mx-3 mb-3 mt-3' : 'mx-6 mb-6 mt-6'
          }`}
          style={{
            background: 'rgba(255, 255, 255, 0.52)',
            borderColor: 'rgba(255, 255, 255, 0.42)'
          }}
        >
          {inner}
        </GlassPanel>
      )
    }
    return <div className="flex min-h-0 flex-1 flex-col bg-whiteout">{inner}</div>
  }

  return (
    <BackgroundContainer className="relative flex h-full w-full min-h-0 flex-col">
      <ProcessingModal
        isVisible={isProcessing}
        title={t('edit.processing', 'Processing...')}
        message={t('edit.processingMessage', 'Please wait while we process your strip')}
        primaryColor={primaryColor}
      />

      <ProcessingModal
        isVisible={isGeneratingStrip}
        title={t('edit.generatingStrip', 'Generating Strip...')}
        message={t('edit.generatingStripMessage', 'Please wait while we prepare your photo strip')}
        primaryColor={primaryColor}
      />

      {/* Shared countdown — same chrome as Select frame mode (theme-aware) */}
      {hasGlobalTimeout && renderEditSharedSessionTimer()}

      {editShell(
        <>
          {/* Header */}
          <div
            className={`flex-shrink-0 border-b ${editGlass ? 'border-carbon/30' : 'border-gray-200'} ${editHeaderPadClass}`}
          >
            <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h1 className={editTitleClass}>
              {t('edit.title', 'Edit Your Photos')}
            </h1>
            <p className={editSubtitleVisibleClass}>
              {t('edit.subtitle', 'Add stickers to your photo strip')}
            </p>
          </div>
          {!hasGlobalTimeout && editTimeout > 0 && (
            <div className="shrink-0 text-right">
              <div className={editHeaderTimerClass}>
                {remainingTime}
              </div>
            </div>
          )}
            </div>
          </div>

          {/* Main Content */}
          <div className={editMainContentClass}>
            {/* Editor canvas */}
            <div
              className={`flex min-h-0 flex-1 flex-col ${stackEdit ? '' : 'min-w-0'}`}
            >
            <div
              className={`relative flex-1 overflow-hidden rounded-lg border ${
                  editGlass
                    ? 'border-carbon/20 bg-white/90 shadow-lg backdrop-blur-md'
                    : 'border-gray-300 bg-white'
                }`}
              >
            {/* Floating Undo/Redo/Delete */}
            <div
              className={`absolute z-10 flex ${editFloatingToolbarMobile ? 'flex-col' : 'flex-row'} ${editToolbarFloatClass}`}
            >
              <button
                type="button"
                onClick={handleUndo}
                disabled={undoStack.length === 0}
                title={t('edit.undo', 'Undo')}
                className={`${editToolbarBtnClass} ${undoStack.length === 0 ? 'cursor-not-allowed opacity-30' : editToolbarIconEnabled}`}
              >
                <FaUndo className={iconSize} />
              </button>
              <button
                type="button"
                onClick={handleRedo}
                disabled={redoStack.length === 0}
                title={t('edit.redo', 'Redo')}
                className={`${editToolbarBtnClass} ${redoStack.length === 0 ? 'cursor-not-allowed opacity-30' : editToolbarIconEnabled}`}
              >
                <FaRedo className={iconSize} />
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={!selectedItemId}
                title={t('edit.delete', 'Delete Selected Item')}
                className={`${editToolbarBtnClass} ${!selectedItemId ? 'cursor-not-allowed opacity-30' : editToolbarIconEnabled}`}
              >
                <FaTrash className={iconSize} />
              </button>
            </div>
            {stripUrl ? (
              <StickerEditor
                stripUrl={stripUrl}
                stickerPlacements={stickerPlacements}
                onStickerPlacement={handleStickerPlacement}
                stickers={availableStickers}
                className="w-full h-full"
                brushStrokes={brushStrokes}
                onBrushStroke={handleBrushStroke}
                brushColor={brushColor}
                brushSize={brushSize}
                brushType={selectedBrushType}
                mode={editMode}
                selectedItemId={selectedItemId}
                onItemSelect={handleItemSelect}
                onStickerUpdate={handleStickerUpdate}
                onBrushStrokeUpdate={handleBrushStrokeUpdate}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <div className={`text-xl ${editGlass ? 'font-medium text-carbon/95' : 'text-carbon/60'}`}>
                  Loading photo strip...
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Controls / sticker picker */}
        <div className={`${editSidebarShellClass} ${sidebarWidthClass}`}>
          {/* Tab Navigation */}
          <div className={`shrink-0 border-b ${editTabRowBorder}`}>
            <div className="-mb-px flex">
              <button
                type="button"
                onClick={() => setEditMode('sticker')}
                className={`flex-1 border-b-2 py-3 px-4 text-center text-sm font-medium transition-colors ${
                  editMode === 'sticker' ? '' : editTabInactiveClass
                }`}
                style={
                  editMode === 'sticker'
                    ? { color: primaryColor, borderBottomColor: primaryColor }
                    : undefined
                }
              >
                {t('edit.stickerMode', 'Sticker')}
              </button>
              <button
                type="button"
                onClick={() => setEditMode('brush')}
                className={`flex-1 border-b-2 py-3 px-4 text-center text-sm font-medium transition-colors ${
                  editMode === 'brush' ? '' : editTabInactiveClass
                }`}
                style={
                  editMode === 'brush'
                    ? { color: primaryColor, borderBottomColor: primaryColor }
                    : undefined
                }
              >
                {t('edit.brushMode', 'Brush')}
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch] pr-0.5">
            {/* Sticker Mode Panel */}
            {editMode === 'sticker' && (
              <>
                {/* Sticker Size Slider */}
                <div>
                  <h3 className={`font-semibold text-carbon ${sectionTitleClass}`}>
                    {t('edit.stickerSize', 'Sticker Size')}
                  </h3>
                  <div className="space-y-2">
                    <label htmlFor="sticker-size-slider" className="sr-only">
                      {t('edit.stickerSize', 'Sticker Size')}
                    </label>
                    <input
                      id="sticker-size-slider"
                      type="range"
                      min={minSize}
                      max={maxSize}
                      value={stickerSize}
                      onChange={(e) => setStickerSize(parseInt(e.target.value, 10))}
                      className={`h-2 w-full cursor-pointer appearance-none rounded-lg ${editSliderTrackClass}`}
                      style={{ accentColor: primaryColor }}
                      title={t('edit.stickerSize', 'Sticker Size')}
                    />
                    <div className={editMetaRowClass}>
                      <span>{minSize}px</span>
                      <span className="font-semibold text-carbon">{stickerSize}px</span>
                      <span>{maxSize}px</span>
                    </div>
                  </div>
                </div>

                {/* Sticker Selection */}
                <div className="mt-2">
                  <h3 className={`font-semibold text-carbon ${sectionTitleClass}`}>
                    {t('edit.selectSticker', 'Select Sticker')}
                  </h3>
                  <div
                    className={`rounded-lg border ${editInsetPanelClass} ${stackEdit ? 'p-2' : 'p-4'}`}
                  >
                    <div className={stickerGridClass}>
                      {availableStickers.map((sticker) => {
                        // Prioritize cached blob URLs over CDN URLs
                        // If thumbnail exists and is cached (blob URL), use it
                        // Otherwise, use cached url (blob URL) if available
                        // Fallback to original thumbnail or url
                        const hasCachedThumbnail = sticker.thumbnail && sticker.thumbnail.startsWith('blob:')
                        const hasCachedUrl = sticker.url && sticker.url.startsWith('blob:')
                        const stickerThumbnail = hasCachedThumbnail
                          ? sticker.thumbnail
                          : hasCachedUrl
                            ? sticker.url
                            : sticker.thumbnail || sticker.url || ''

                        return (
                          <StickerThumbnail
                            key={sticker._id}
                            sticker={sticker}
                            thumbnail={stickerThumbnail || 'sticker'}
                            isSelected={selectedStickerId === sticker._id}
                            onSelect={() => setSelectedStickerId(sticker._id)}
                            t={t}
                            accentColor={primaryColor}
                          />
                        )
                      })}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Brush Mode Panel */}
            {editMode === 'brush' && (
              <>
                {/* Brush Size */}
                <div>
                  <h3 className={`font-semibold text-carbon ${sectionTitleClass}`}>
                    {t('edit.brushSize', 'Brush Size')}
                  </h3>
                  <div className="space-y-2">
                    <label htmlFor="brush-size-slider" className="sr-only">
                      {t('edit.brushSize', 'Brush Size')}
                    </label>
                    <input
                      id="brush-size-slider"
                      type="range"
                      min={1}
                      max={50}
                      value={brushSize}
                      onChange={(e) => setBrushSize(parseInt(e.target.value, 10))}
                      className={`h-2 w-full cursor-pointer appearance-none rounded-lg ${editSliderTrackClass}`}
                      style={{ accentColor: primaryColor }}
                      title={t('edit.brushSize', 'Brush Size')}
                    />
                    <div className={editMetaRowClass}>
                      <span>1px</span>
                      <span className="font-semibold text-carbon">{brushSize}px</span>
                      <span>50px</span>
                    </div>
                  </div>
                </div>

                {/* Brush Color */}
                <div className="mt-2">
                  <h3 className={`font-semibold text-carbon ${sectionTitleClass}`}>
                    {t('edit.brushColor', 'Brush Color')}
                  </h3>
                  <div className="space-y-2">
                  {/* Large color indicator that opens custom color picker */}
                  <div className="flex items-center justify-center">
                    <button
                      type="button"
                      onClick={() => colorPickerRef.current?.click()}
                      className="cursor-pointer rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
                      style={{ ['--tw-ring-color' as string]: primaryColor } as React.CSSProperties}
                      title={t('edit.selectCustomColor', 'Click to select custom color')}
                    >
                      <div
                        className="h-12 w-12 rounded-lg border-2 border-gray-300 shadow-sm transition-shadow hover:shadow-md"
                        style={{ backgroundColor: brushColor }}
                      />
                    </button>
                    <input
                      ref={colorPickerRef}
                      id="brush-color-picker"
                      type="color"
                      value={brushColor}
                      onChange={(e) => setBrushColor(e.target.value)}
                      className="sr-only"
                      title={t('edit.brushColor', 'Brush Color')}
                    />
                  </div>
                  
                  {/* Rainbow color palette - Google Docs style */}
                  <div>
                    {/* Rainbow gradient strip */}
                    <div className="mb-2">
                      <div className="h-4 rounded border border-gray-300 overflow-hidden flex">
                        {Array.from({ length: 24 }, (_, i) => {
                          const hue = (i * 360) / 24
                          // Convert HSL to hex for onClick
                          const hslToHex = (h: number, s: number, l: number): string => {
                            l /= 100
                            const a = (s * Math.min(l, 1 - l)) / 100
                            const f = (n: number): string => {
                              const k = (n + h / 30) % 12
                              const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
                              return Math.round(255 * color).toString(16).padStart(2, '0')
                            }
                            return `#${f(0)}${f(8)}${f(4)}`
                          }
                          return (
                            <button
                              key={i}
                              type="button"
                              onClick={() => {
                                const hex = hslToHex(hue, 100, 50)
                                setBrushColor(hex)
                              }}
                              className="flex-1 h-full hover:opacity-80 transition-opacity"
                              style={{
                                backgroundColor: `hsl(${hue}, 100%, 50%)`
                              }}
                              title={`Hue: ${Math.round(hue)}°`}
                            />
                          )
                        })}
                      </div>
                    </div>
                    
                    {/* Color grid organized by hue with varying saturation/lightness */}
                    <div className="grid grid-cols-8 gap-1">
                      {/* Red shades */}
                      {['#FFEBEE', '#FFCDD2', '#EF9A9A', '#E57373', '#EF5350', '#F44336', '#E53935', '#C62828'].map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setBrushColor(color)}
                          className={`h-5 w-5 rounded transition-all hover:scale-110 ${
                            brushColor.toUpperCase() === color.toUpperCase()
                              ? 'scale-105 border-2'
                              : 'border border-gray-300 hover:border-gray-400'
                          }`}
                          style={{
                            backgroundColor: color,
                            ...(brushColor.toUpperCase() === color.toUpperCase()
                              ? {
                                  borderColor: primaryColor,
                                  boxShadow: `0 0 0 2px ${primaryColor}55`
                                }
                              : {})
                          }}
                          title={color.toUpperCase()}
                        />
                      ))}
                      {/* Orange shades */}
                      {['#FFF3E0', '#FFE0B2', '#FFCC80', '#FFB74D', '#FFA726', '#FF9800', '#FB8C00', '#E65100'].map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setBrushColor(color)}
                          className={`h-5 w-5 rounded transition-all hover:scale-110 ${
                            brushColor.toUpperCase() === color.toUpperCase()
                              ? 'scale-105 border-2'
                              : 'border border-gray-300 hover:border-gray-400'
                          }`}
                          style={{
                            backgroundColor: color,
                            ...(brushColor.toUpperCase() === color.toUpperCase()
                              ? {
                                  borderColor: primaryColor,
                                  boxShadow: `0 0 0 2px ${primaryColor}55`
                                }
                              : {})
                          }}
                          title={color.toUpperCase()}
                        />
                      ))}
                      {/* Yellow shades */}
                      {['#FFFDE7', '#FFF9C4', '#FFF59D', '#FFF176', '#FFEE58', '#FFEB3B', '#FDD835', '#F9A825'].map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setBrushColor(color)}
                          className={`h-5 w-5 rounded transition-all hover:scale-110 ${
                            brushColor.toUpperCase() === color.toUpperCase()
                              ? 'scale-105 border-2'
                              : 'border border-gray-300 hover:border-gray-400'
                          }`}
                          style={{
                            backgroundColor: color,
                            ...(brushColor.toUpperCase() === color.toUpperCase()
                              ? {
                                  borderColor: primaryColor,
                                  boxShadow: `0 0 0 2px ${primaryColor}55`
                                }
                              : {})
                          }}
                          title={color.toUpperCase()}
                        />
                      ))}
                      {/* Green shades */}
                      {['#E8F5E9', '#C8E6C9', '#A5D6A7', '#81C784', '#66BB6A', '#4CAF50', '#43A047', '#2E7D32'].map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setBrushColor(color)}
                          className={`h-5 w-5 rounded transition-all hover:scale-110 ${
                            brushColor.toUpperCase() === color.toUpperCase()
                              ? 'scale-105 border-2'
                              : 'border border-gray-300 hover:border-gray-400'
                          }`}
                          style={{
                            backgroundColor: color,
                            ...(brushColor.toUpperCase() === color.toUpperCase()
                              ? {
                                  borderColor: primaryColor,
                                  boxShadow: `0 0 0 2px ${primaryColor}55`
                                }
                              : {})
                          }}
                          title={color.toUpperCase()}
                        />
                      ))}
                      {/* Blue shades */}
                      {['#E3F2FD', '#BBDEFB', '#90CAF9', '#64B5F6', '#42A5F5', '#2196F3', '#1E88E5', '#1565C0'].map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setBrushColor(color)}
                          className={`h-5 w-5 rounded transition-all hover:scale-110 ${
                            brushColor.toUpperCase() === color.toUpperCase()
                              ? 'scale-105 border-2'
                              : 'border border-gray-300 hover:border-gray-400'
                          }`}
                          style={{
                            backgroundColor: color,
                            ...(brushColor.toUpperCase() === color.toUpperCase()
                              ? {
                                  borderColor: primaryColor,
                                  boxShadow: `0 0 0 2px ${primaryColor}55`
                                }
                              : {})
                          }}
                          title={color.toUpperCase()}
                        />
                      ))}
                      {/* Purple shades */}
                      {['#F3E5F5', '#E1BEE7', '#CE93D8', '#BA68C8', '#AB47BC', '#9C27B0', '#8E24AA', '#6A1B9A'].map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setBrushColor(color)}
                          className={`h-5 w-5 rounded transition-all hover:scale-110 ${
                            brushColor.toUpperCase() === color.toUpperCase()
                              ? 'scale-105 border-2'
                              : 'border border-gray-300 hover:border-gray-400'
                          }`}
                          style={{
                            backgroundColor: color,
                            ...(brushColor.toUpperCase() === color.toUpperCase()
                              ? {
                                  borderColor: primaryColor,
                                  boxShadow: `0 0 0 2px ${primaryColor}55`
                                }
                              : {})
                          }}
                          title={color.toUpperCase()}
                        />
                      ))}
                      {/* Pink shades */}
                      {['#FCE4EC', '#F8BBD0', '#F48FB1', '#F06292', '#EC407A', '#E91E63', '#D81B60', '#AD1457'].map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setBrushColor(color)}
                          className={`h-5 w-5 rounded transition-all hover:scale-110 ${
                            brushColor.toUpperCase() === color.toUpperCase()
                              ? 'scale-105 border-2'
                              : 'border border-gray-300 hover:border-gray-400'
                          }`}
                          style={{
                            backgroundColor: color,
                            ...(brushColor.toUpperCase() === color.toUpperCase()
                              ? {
                                  borderColor: primaryColor,
                                  boxShadow: `0 0 0 2px ${primaryColor}55`
                                }
                              : {})
                          }}
                          title={color.toUpperCase()}
                        />
                      ))}
                      {/* Grayscale */}
                      {['#FFFFFF', '#F5F5F5', '#EEEEEE', '#E0E0E0', '#BDBDBD', '#9E9E9E', '#616161', '#000000'].map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setBrushColor(color)}
                          className={`h-5 w-5 rounded transition-all hover:scale-110 ${
                            brushColor.toUpperCase() === color.toUpperCase()
                              ? 'scale-105 border-2'
                              : 'border border-gray-300 hover:border-gray-400'
                          }`}
                          style={{
                            backgroundColor: color,
                            ...(brushColor.toUpperCase() === color.toUpperCase()
                              ? {
                                  borderColor: primaryColor,
                                  boxShadow: `0 0 0 2px ${primaryColor}55`
                                }
                              : {})
                          }}
                          title={color.toUpperCase()}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                </div>

                {/* Brush Type Selection */}
                <div className="mt-2 pb-1">
                  <h3 className={`font-semibold text-carbon ${sectionTitleClass}`}>
                    {t('edit.selectBrushType', 'Select Brush Type')}
                  </h3>
                  <div
                    className={`rounded-lg border ${editInsetPanelClass} ${stackEdit ? 'p-2' : 'p-4'}`}
                  >
                    <div className={brushTypeGridClass}>
                      {(
                        ['solid', 'dashed', 'dotted', 'marker', 'pencil', 'airbrush'] as BrushType[]
                      ).map((brushType) => (
                        <BrushTypePreview
                          key={brushType}
                          brushType={brushType}
                          brushColor={brushColor}
                          brushSize={brushSize}
                          isSelected={selectedBrushType === brushType}
                          onSelect={() => setSelectedBrushType(brushType)}
                          accentColor={primaryColor}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className={`shrink-0 ${editViewportLandscape ? 'pt-1.5' : 'pt-2'}`}>
            <Button
              onClick={handleContinue}
              text={t('edit.continue', 'Continue')}
              data-nav-to="user-reprint"
              disabled={isProcessing || isGeneratingStrip}
              className={editContinueBtnClass}
              variant="primary"
            />
          </div>
        </div>
      </div>
        </>
      )}

      {/* Hidden PrintPreview for generating strip */}
      <div className="hidden">
        <PrintPreview
          ref={previewRef}
          frameConfig={frameConfig}
          selectedPhotos={capturedPhotos}
          isInteractive={false}
          sessionId={galleryKey || gallery.id}
        />
      </div>
    </BackgroundContainer>
  )
}

export default EditPage
